import "../../../App.scss";
import "./Styles.scss";
import { LogOutReason } from "@iconics/core";
import { appContext } from "../../../AppContext";
import { IUserDetails, INodeData } from "../../../Providers.LocalStorage/Models";
import { PagedResponse } from "@iconics/core";
import { IRouterProps, Typography, withRouting } from "@iconics/components";
import { IUserRights } from "@iconics/core";
import { IbssComponent } from "../../../Components/Core/BaseComponent/IbssComponent";
import { Tokens } from "@okta/okta-auth-js";
import { ApiConstants } from "@iconics/core";
import { RootNodeId } from "../../../Providers.StaticData/StaticDataProvider";
import { JSX } from "react";
import { User } from "./DataModels";

class LoginSuccess extends IbssComponent<IRouterProps, IState>
{
    private get labels() { return appContext().labels; }
    private get local() { return appContext().localStorageProvider; }

    constructor(props: IRouterProps)
    {
        super(props);
        this.state = {};
    }

    public async componentDidMount(): Promise<void>
    {
        await this.handleLogin();
    }

    private async handleLogin(): Promise<void>
    {
        try
        {
            let result: ILoginResult;
            const user = this.local.getUserDetails();
            switch (user.providerType)
            {
                case "Native":
                    result = {
                        userEmail: user.email,
                        displayName: user.displayName,
                        rightsData: await this.getRights()
                    }
                    break;

                case "AAD":
                    result = await this.handleActiveDirectoryLogin();
                    break;

                case "Okta":
                    result = await this.handleOktaLogin();
                    break;

                case "Auth0":
                    result = await this.handleAuth0Login();
                    break;

                default:
                    appContext().authentication.logOut();
                    return;
            }

            const displayName = await this.getDisplayName(result.userEmail);

            const userDetail: IUserDetails = {
                ...user,
                displayName: displayName ?? result.displayName,
                email: result.userEmail,
                userRights: result.rightsData
            }
            this.local.setUserDetails(userDetail);
            await this.cacheData();
            this.props.navigate('/admin/home', { replace: true });
        }
        catch (error)
        {
            console.error(error);
            await appContext().authentication.logOut(LogOutReason.LoginFailed);
        }
    }

    private async getDisplayName(userEmail: string): Promise<string | undefined>
    {
        try
        {
            const responses = await appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.get<PagedResponse<User[]>>({
                tenancy: appContext().tenantId,
                filter: `Email eq '${userEmail}'`,
                select: 'PreferredName,DisplayName,FirstName,LastName,Email',
                top: 1
            });

            const response = responses.value[0];
            if (response.PreferredName?.trim())
            {
                return response.PreferredName.trim();
            }

            if (response.DisplayName?.trim())
            {
                return response.DisplayName.trim();
            }

            if (response.FirstName?.trim() && response.LastName?.trim())
            {
                return `${response.FirstName.trim()} ${response.LastName.trim()}`;
            }

            if (response.FirstName?.trim())
            {
                return response.FirstName.trim();
            }

            if (response.LastName?.trim())
            {
                return response.LastName.trim();
            }

            return response.Email;
        }
        catch 
        {
            return undefined;
        }
    }

    private getRights(application?: string): Promise<IUserRights>
    {
        return appContext().fadxPlatformApiClientV2.v2.security.byTenancy.users.applicationActions.get({
            tenancy: appContext().tenantId,
            application: application,
        });
    }

    private async handleActiveDirectoryLogin(): Promise<ILoginResult>
    {
        const msal = appContext().msalProvider;
        const account = msal.getAccount();
        const authResult = await msal.getToken();
        appContext().authentication.setToken(authResult.accessToken);
        const rights = await this.getRights();

        const result: ILoginResult =
        {
            userEmail: account?.username ?? '',
            displayName: account?.name ?? "",
            rightsData: rights,
        }
        return Promise.resolve(result);
    }

    private async handleOktaLogin(): Promise<ILoginResult>
    {
        const tokens: Tokens = JSON.parse(localStorage.getItem('okta-token-storage') || '{}');
        const idToken = tokens.idToken;

        if (!idToken || !idToken.claims.email || !idToken.claims.name)
        {
            throw new Error("Bad OKTA token.");
        }

        appContext().authentication.setToken(idToken.idToken);
        const rights = await this.getRights();

        const result: ILoginResult =
        {
            userEmail: idToken.claims.email,
            displayName: idToken.claims.name,
            rightsData: rights,
        }
        return Promise.resolve(result);
    }

    private async handleAuth0Login(): Promise<ILoginResult>
    {
        const auth0 = appContext().auth0Provider;
        const token = await auth0.getToken();
        const user = await auth0.getUser();

        if (!token || !user?.email || !user?.name)
        {
            throw new Error('Auth0 login failed.');
        }

        appContext().authentication.setToken(token);
        const rights = await this.getRights();

        return {
            userEmail: user.email,
            displayName: user.name,
            rightsData: rights,
        };
    }

    private async cacheData(): Promise<void>
    {
        // cache nodes
        const nodesData: INodeData[] = [];
        let skipToken = '';

        do
        {
            const pageOfNodes = await appContext().fadxPlatformApiClientV2.v2.core.byTenancy.nodes.get<PagedResponse<INodeData[]>>({
                tenancy: appContext().tenantId,
                filter: `(ID eq ${RootNodeId} or Parent_ID ne null) and Deleted eq false and IsEnabled eq 1`,
                orderBy: 'SortOrder',
                top: ApiConstants.mediumODataTop,
                skipToken: skipToken,
            });

            // had to default value and skip-token due to bug in API on this endpoint only where it returns {} if page contains exactly 0 items
            nodesData.push(...pageOfNodes.value ?? []);
            skipToken = pageOfNodes.skipToken ?? '';
        }
        while (skipToken !== '');

        this.local.setNodeData(nodesData);

        // cache right list
        const ibssRightList = await this.getRights('ADMINPORTAL');
        this.local.setIbssRights(ibssRightList);
    }

    public render(): JSX.Element
    {
        return (
            <div>
                <div className="redirectContainer">
                    <div className="title">
                        <Typography role="title" size="medium">{this.labels.Platform_RedirectionStatus_Title}</Typography>
                    </div>
                    <div className="subtitle">
                        <Typography role="body" size="medium">{this.labels.Platform_RedirectionStatus_SubTitle} <span id="countdown"></span></Typography>
                    </div>
                </div>
            </div>
        );
    }
}

export default withRouting(LoginSuccess);

export interface ILoginResult
{
    userEmail: string;
    displayName: string;
    rightsData: IUserRights;
}

export interface IState { }
